import { IconAlignLeft, IconBulb, IconPaperclip, IconPhoto, IconRobot } from '@tabler/icons-react';
import { Flex, TextInput } from '@mantine/core';
import Delta from 'quill-delta';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPopupGenerationPrompt } from '../../constants/GeneratedContent';
import useFetch from '../../helpers/useFetch';
import { validateUrlProtocol } from '../../helpers/validateURL';
import AiChatFastContextModal from '../Modals/AiChatFastContextModal';

const GenerateContentPopup = ({
  generatePopupStyles,
  hideGeneratePopup,
  setIsLoading,
  quillEditorRef,
  lastHoveredElementIndex,
  aiModel,
  provider,
  setUserError,
  controllerRef,
  setShowAbortButton,
  handleBrandContextChange,
}) => {
  const customFetch = useFetch();
  const { domain } = useSelector((state) => state.domain);
  const [generatePopupClass, setGeneratePopupClass] = useState('');
  const [prompt, setPrompt] = useState({ type: '', value: '' });
  const [refImageUrl, setRefImageUrl] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [orientation, setOrientation] = useState('landscape');
  const [contextModalOpen, setContextModalOpen] = useState(false);
  const [contextSelection, setContextSelection] = useState();

  const sleep = (seconds) => new Promise((resolve) => setTimeout(resolve, seconds * 1000));

  useEffect(() => {
    if (generatePopupClass === 'generate-content-popup--image') {
      const mdxEditor = document.querySelector('.ql-editor');
      const hoveredElement = mdxEditor.children[lastHoveredElementIndex];
      const nextElement = hoveredElement ? hoveredElement.nextElementSibling : null;

      if (nextElement) {
        const nextElementIndex = Array.prototype.indexOf.call(mdxEditor.children, nextElement);
        const nextElementFound = mdxEditor.children[nextElementIndex];
        nextElementFound.style['padding-top'] = '100px';
      }

      // Cleanup function to remove padding when the popup is removed
      return () => {
        if (nextElement) {
          nextElement.style['padding-top'] = '';
        }
      };
    }
  }, [generatePopupClass, lastHoveredElementIndex]);

  const unlockEditor = () => {
    hideGeneratePopup();
    setIsLoading(false);
  };

  const handleChange = (e) => {
    setPrompt({ type: e.target.name, value: e.target.value });
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleEnterClick = (e) => {
    if (e.key === 'Enter' && e.target.value.trim() !== '') {
      if (e.target.name === 'text') {
        generateText();
      } else {
        generateImage();
      }
    }
  };

  const generateAiText = async (body, callback) => {
    setUserError(false);
    setIsLoading(true);
    setShowAbortButton(true);

    if (contextSelection) {
      body.contextSelection = contextSelection;
    }

    try {
      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      const response = await fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/prompt/stream`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(body),
        signal,
      });

      if (response.ok) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let result;
        while (!result?.done) {
          unlockEditor();

          result = await reader.read();
          let chunk = decoder.decode(result.value);
          await callback(chunk);
        }
      } else {
        setUserError({ message: `Error: ${response.statusText}` });
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        setUserError(error);
      }
    } finally {
      unlockEditor();
      setShowAbortButton(false);
    }
  };

  const generateAiImage = async (body, callback) => {
    setIsLoading(true);

    const startingUrl = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/prompt`;

    let url = startingUrl;
    url += '/generateImage';
    body.orientation = orientation;

    if (refImageUrl) {
      body.refImage = refImageUrl;
    }

    try {
      const response = await customFetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!response?.imageName) {
        console.log('Unexpected error, please try again!');
        unlockEditor();
        return;
      }

      let checkPromptRessponseUrl = startingUrl;

      checkPromptRessponseUrl += `/generatedImage?` + new URLSearchParams({ name: response.imageName });

      let retries = 0;
      // 10 minutes maximum retries
      while (retries < 600) {
        const data = await customFetch(checkPromptRessponseUrl);
        if (data.imageUrl) {
          if (validateUrlProtocol(data.imageUrl)) {
            unlockEditor();
            await callback(data);
            break;
          } else {
            throw new Error(`Error: ${data.imageUrl}`);
          }
        }

        retries += 1;
        await sleep(1);
      }
    } catch (error) {
      setUserError(error);
      unlockEditor();
    }
  };

  const getCurrentInsertionPoint = () => {
    let currentInsertionPoint = 0;
    const contentsGenDelta = new Delta(quillEditorRef.current.getEditor().getContents());

    contentsGenDelta.eachLine((line, attributes, i) => {
      if (i === lastHoveredElementIndex) {
        currentInsertionPoint += line.length();
        currentInsertionPoint += i;
        return false;
      }

      currentInsertionPoint += line.length();
    });
    return currentInsertionPoint;
  };

  const generateText = async () => {
    if (!prompt.value) {
      return;
    }

    const text = quillEditorRef.current.getEditor().getText();
    const textLines = text.split('\n');
    const firstPart = textLines.slice(0, lastHoveredElementIndex + 1).join('\n');
    const secondPart = textLines.slice(lastHoveredElementIndex + 1).join('\n');
    const finalPrompt = getPopupGenerationPrompt(prompt.value, firstPart, secondPart, 'en');
    let currentInsertionPoint = getCurrentInsertionPoint();

    const editor = quillEditorRef.current.getEditor();

    let isFirst = true;

    generateAiText({ prompt: finalPrompt, provider: provider, model: aiModel, isAiHub: true }, async (data) => {
      if (isFirst) {
        editor.insertText(currentInsertionPoint, '\n', 'user');
        currentInsertionPoint += 1;
        isFirst = false;
      }

      editor.setSelection(currentInsertionPoint);
      editor.insertText(currentInsertionPoint, data, 'user');
      currentInsertionPoint += data.length;
    });
  };

  const generateImage = async () => {
    generateAiImage({ name: 'Editor generated image', prompt: prompt.value }, async (data) => {
      const firstPartTextLength = getCurrentInsertionPoint();
      const newDelta = new Delta().retain(firstPartTextLength).insert({ image: data.imageUrl });
      quillEditorRef.current.getEditor().updateContents(newDelta, 'user');
    });
  };

  const closeContextModal = () => {
    setContextModalOpen(false);

    setContextSelection((prev) => {
      if (!prev) {
        return {};
      }

      const contextSelection = structuredClone(prev);
      delete contextSelection.coPilots;

      const textToInsert = Object.entries(contextSelection)
        .map(([key, values]) => {
          if (!values || !values.length) {
            return '';
          }

          if (key === 'userPrompts') {
            return values.map((v) => v.description + '\n\n').join('');
          }

          return `${key[0].toUpperCase() + key.slice(1)}:\n${values.join('\n')}\n\n`;
        })
        .join('');

      const currentInsertionPoint = getCurrentInsertionPoint();
      const editor = quillEditorRef.current.getEditor();
      editor.insertText(currentInsertionPoint, '\n', 'user');
      editor.setSelection(currentInsertionPoint + 1);
      editor.insertText(currentInsertionPoint, textToInsert, 'user');

      const newContextSelection = structuredClone(prev);
      newContextSelection.archetypes = [];
      newContextSelection.audiences = [];
      newContextSelection.description = [];
      newContextSelection.tones = [];
      newContextSelection.voices = [];
      newContextSelection.userPrompts = [];

      return newContextSelection;
    });
  };

  return (
    <div className={`flex-column generate-content-popup ${generatePopupClass}`} style={generatePopupStyles}>
      <div className="generate-content-popup-choice">
        <button onClick={() => setContextModalOpen(true)}>
          <IconBulb size={14} /> Include Context
        </button>

        <button onClick={() => setGeneratePopupClass('generate-content-popup--text')}>
          <IconAlignLeft size={14} /> Generate Text
        </button>

        <button onClick={() => setGeneratePopupClass('generate-content-popup--image')}>
          <IconPhoto size={14} /> Generate Image
        </button>
      </div>

      <div className="generate-text-content">
        <input
          type="text"
          name="text"
          value={prompt.value}
          onChange={handleChange}
          onKeyDown={handleEnterClick}
          placeholder="Tell me what to write and click enter"
        />

        <button onClick={generateText} className={`${prompt.value.trim() !== '' ? 'active' : ''}`}>
          <IconRobot size={14} />
        </button>
      </div>
      {generatePopupClass === 'generate-content-popup--image' && (
        <Flex gap="xs" style={{ marginTop: 'var(--mantine-spacing-xs)' }}>
          <button
            className={`p5 button-element ${orientation === 'landscape' ? 'active' : ''}`}
            style={{ color: 'var(--mantine-primary-color-filled)' }}
            onClick={() => setOrientation('landscape')}
          >
            Landscape
          </button>
          <button
            className={`p5 button-element ${orientation === 'portrait' ? 'active' : ''}`}
            style={{ color: 'var(--mantine-primary-color-filled)' }}
            onClick={() => setOrientation('portrait')}
          >
            Portrait
          </button>
        </Flex>
      )}

      <div className="generate-image-content" style={{ marginTop: 'var(--mantine-spacing-xs)', marginBottom: 'var(--mantine-spacing-xs)' }}>
        <Flex id="input-small-container" style={{ padding: 0, marginRight: 'var(--mantine-spacing-xs)' }}>
          <TextInput
            type="text"
            placeholder="Tell me what to draw and click enter"
            value={prompt.value}
            onChange={handleChange}
          />
          <IconRobot
            size={18}
            className={`button-element ${prompt.value.trim() !== '' ? 'active' : ''}`}
            style={{ padding: 10, width: 18, height: 18, marginLeft: 4, color: 'var(--mantine-primary-color-filled)', cursor: 'pointer' }}
            onClick={generateImage}
          />
        </Flex>
      </div>

      <div className="generate-image-content">
        <Flex id="input-small-container" style={{ padding: 0, marginRight: 'var(--mantine-spacing-xs)' }}>
          <TextInput
            type="text"
            placeholder="Input the URL of a reference image or upload one"
            value={refImageUrl || ''}
            onChange={(e) => setRefImageUrl(e.target.value)}
          />
          <IconPaperclip
            size={18}
            className={`button-element ${refImageUrl ? 'active' : ''}`}
            style={{ padding: 10, width: 18, height: 18, marginLeft: 4, color: 'var(--mantine-primary-color-filled)', cursor: 'pointer' }}
            onClick={toggleModal}
          />
        </Flex>
      </div>

      {contextModalOpen && (
        <AiChatFastContextModal
          brandContext={domain?.brandContext}
          selectedContext={contextSelection}
          setSelectedContext={setContextSelection}
          closeModal={closeContextModal}
          handleBrandContextChange={handleBrandContextChange}
          selectedText={null}
          setSelectedText={null}
        />
      )}
    </div>
  );
};

export default GenerateContentPopup;
